import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { AlertTriangle, CheckCircle2, XCircle, Users, TrendingDown, Compass, Sparkles } from "lucide-react";

/**
 * Mentor Digest Preview — Vista do Secretário of their mentees (players who picked
 * this coach explicitly). Same shape as the Sunday-night email but rendered
 * inline in /my-consultants. Hides silently if the coach has no mentees.
 */
export default function MentorDigestWidget() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/mentor/digest/preview")
      .then((r) => setData(r.data))
      .catch(() => setErr("not_coach"));
  }, []);

  if (err || !data) return null;
  const mentees = data.mentees || [];
  if (mentees.length === 0) return null;

  const eur = (n) => `${Math.round(Number(n) || 0).toLocaleString("pt-PT")} €`;
  const agg = data.aggregate || {};

  // Sort: most flags first
  const sorted = [...mentees].sort((a, b) => (b.flags?.length || 0) - (a.flags?.length || 0));

  return (
    <div className="kw-card p-0 overflow-hidden" data-testid="mentor-digest-widget">
      <div className="p-5 border-b border-[#222] flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="kw-overline flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-[#3B82F6]" /> Mentor Digest · Os teus mentorandos
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mt-1">
            Quem <span className="text-[#3B82F6]">te escolheu</span>
          </h2>
          <p className="text-xs text-[#A3A3A3] mt-1">
            Semana {data.week_num} · {data.month_label} · Prioridade: quem precisa de atenção fica no topo
          </p>
        </div>
        <div className="grid grid-cols-3 gap-0 min-w-[300px]" data-testid="mentor-digest-kpis">
          <MiniKpi label="Total" value={agg.total || 0} color="#FFF" />
          <MiniKpi label="Atenção" value={agg.needs_attention || 0} color={agg.needs_attention ? "#E50000" : "#22C55E"} />
          <MiniKpi label="Alinhados" value={agg.on_track || 0} color="#22C55E" />
        </div>
      </div>
      <div className="p-5 space-y-3" data-testid="mentor-digest-list">
        {sorted.map((m) => <MenteeRow key={m.player_id} m={m} eur={eur} />)}
      </div>
    </div>
  );
}

function MiniKpi({ label, value, color = "#FFF" }) {
  return (
    <div className="p-2 border-r border-[#222] last:border-r-0 text-center">
      <div className="text-[9px] uppercase tracking-widest text-[#737373] font-bold">{label}</div>
      <div className="font-display text-2xl font-black" style={{ color }}>{value}</div>
    </div>
  );
}

const FLAG_META = {
  no_report: { label: "Sem report", color: "#E50000", icon: XCircle },
  gps_empty: { label: "GPS vazio", color: "#EAB308", icon: Compass },
  below_target: { label: "Abaixo target", color: "#E50000", icon: TrendingDown },
  no_commitments: { label: "Sem compromissos", color: "#EAB308", icon: AlertTriangle },
};

function MenteeRow({ m, eur }) {
  const flags = m.flags || [];
  const severity = flags.length >= 2 ? "atencao" : (flags.length === 1 ? "olhar" : "ok");
  const style = {
    atencao: { border: "#E50000", bg: "rgba(229,0,0,0.05)", badge: "Atenção", badgeColor: "#E50000" },
    olhar: { border: "#EAB308", bg: "rgba(234,179,8,0.05)", badge: "Olhar", badgeColor: "#EAB308" },
    ok: { border: "#22C55E", bg: "rgba(34,197,94,0.05)", badge: "OK", badgeColor: "#22C55E" },
  }[severity];

  return (
    <div
      className="border-l-2 p-3 flex flex-wrap items-start gap-3"
      style={{ borderColor: style.border, background: style.bg }}
      data-testid={`mentee-row-${m.player_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-bold text-white">{m.name}</div>
          <span
            className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border"
            style={{ color: style.badgeColor, borderColor: style.badgeColor }}
          >
            {style.badge}
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#737373] font-bold mt-0.5">
          {m.club || "—"} · {m.mbti || "—"}
        </div>
        {m.gps_configured && m.ytd_pct !== null && (
          <div className="text-xs text-[#A3A3A3] font-mono mt-1" data-testid={`mentee-ytd-${m.player_id}`}>
            YTD:{" "}
            <span className="font-bold" style={{ color: m.ytd_pct >= 100 ? "#22C55E" : m.ytd_pct >= 85 ? "#EAB308" : "#E50000" }}>
              {m.ytd_pct.toFixed(0)}%
            </span>{" "}
            · Δ{" "}
            <span style={{ color: (m.ytd_delta_eur || 0) >= 0 ? "#22C55E" : "#E50000" }}>
              {(m.ytd_delta_eur || 0) >= 0 ? "+" : ""}{eur(m.ytd_delta_eur || 0)}
            </span>
          </div>
        )}
        {m.monthly_goal && (
          <div className="text-xs italic text-[#E5E5E5] mt-1" data-testid={`mentee-monthly-${m.player_id}`}>
            &quot;{m.monthly_goal}&quot;
          </div>
        )}
        {flags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2" data-testid={`mentee-flags-${m.player_id}`}>
            {flags.map((f) => {
              const meta = FLAG_META[f];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <span
                  key={f}
                  className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border font-bold"
                  style={{ color: meta.color, borderColor: meta.color }}
                >
                  <Icon className="w-2.5 h-2.5" /> {meta.label}
                </span>
              );
            })}
          </div>
        )}
        {flags.length === 0 && (
          <div className="text-xs text-[#22C55E] flex items-center gap-1 mt-1">
            <CheckCircle2 className="w-3 h-3" /> Tudo alinhado
          </div>
        )}
      </div>
      <div className="text-right text-xs text-[#A3A3A3]">
        <div className="text-[10px] uppercase tracking-widest text-[#737373]">Compromissos</div>
        <div className="font-display text-2xl font-black text-white">{m.upcoming_commitments || 0}</div>
        <div className={`inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-widest font-bold ${m.reported_this_week ? "text-[#22C55E]" : "text-[#E50000]"}`}>
          {m.reported_this_week ? <><CheckCircle2 className="w-3 h-3" /> Reportou</> : <><XCircle className="w-3 h-3" /> Falta</>}
        </div>
      </div>
    </div>
  );
}
